'use client'
import { Media } from './Media/Media'
import React from 'react'

import type { GeneralInformation } from '@/payload-types'

import { CMSLink } from './CMSLink'
import { cn } from '@/utilities/ui'
import { Icon } from './Icon'
import RichText from '@onclicks/sharedcomponents/RichText'
import { NewsletterComponent } from './Newsletter'

const getHref = (linkWithMedia: any) => {
  if (linkWithMedia?.url) return linkWithMedia.url

  const value = linkWithMedia?.reference?.value
  if (value && typeof value === 'object') {
    if ('anchor' in value && value.anchor) return `#${value.anchor}`
    if ('slug' in value && value.slug) return `/${value.slug}`
  }
  return ''
}

const paymentMethodIcons: Record<string, string> = {
  bancontact: 'credit-card',
  edenred: 'credit-card',
  pluxee: 'credit-card',
  libracheque: 'credit-card',
  maestro: 'credit-card',
  monizze: 'credit-card',
  vPay: 'credit-card',
  visa: 'cc-visa',
  cash: 'money-bill',
  mastercard: 'cc-mastercard',
}

const paymentMethodLabels: Record<string, string> = {
  bancontact: 'Bancontact',
  edenred: 'Carte Edenred',
  pluxee: 'Carte Pluxee Lunch',
  libracheque: 'Librachèque',
  maestro: 'Maestro',
  monizze: 'Up Monizze',
  vPay: 'VPay',
  visa: 'Visa',
  cash: 'Cash',
  mastercard: 'Mastercard',
}

const Footer: React.FC<GeneralInformation> = (props) => {
  const { footerNav, copyrights, form } = props || {}

  const navColumns = footerNav?.filter((item) => !item.paymentMethods) ?? []
  const paymentColumn = footerNav?.find((item) => item.paymentMethods)

  const formBlockData = form?.[0]
  const newsletterForm =
    formBlockData && typeof formBlockData.form === 'object' ? formBlockData.form : null

  return (
    <>
      <footer className="footer pt-20 pb-6 bg-current/5 bg-gradient-to-br from-[#d966cc] via-[#f4828c] to-[#f5a55a] text-primary">
        <div className="container">
          <div className="grid gap-y-12 sm:grid-cols-2 sm:gap-x-8 lg:grid-cols-5 lg:gap-x-10">
            {navColumns.map((item, index) => {
              const socialNetworks = (item?.socialNetworks as GeneralInformation)?.socialNetworks
              const social = socialNetworks?.[0]?.social

              return (
                <div
                  key={index}
                  className={cn(
                    'flex flex-col items-center text-center md:items-start md:text-left w-full h-fit',
                  )}
                >
                  <div className="flex flex-col w-full">
                    {item.columnTitle && (
                      <h2 className="font-bold text-base mb-4 text-primary">{item.columnTitle}</h2>
                    )}

                    <ul className="flex flex-col justify-center md:justify-start  gap-3 w-full">
                      {item.linksMedia?.map((row, index) => {
                        return (
                          <li
                            key={index}
                            className="flex justify-center md:justify-start w-full list-none"
                          >
                            <div>
                              {row.linkWithMedia?.media && (
                                <Media
                                  imgClassName={
                                    row.linkWithMedia.label || row.linkWithMedia.url
                                      ? `w-8 shrink-0`
                                      : 'w-16'
                                  }
                                  priority
                                  className="mx-auto"
                                  resource={row.linkWithMedia.media}
                                />
                              )}
                            </div>

                            <div className="md:flex-1">
                              {!row.linkWithMedia?.url && row.linkWithMedia?.label && (
                                <span className={cn(`text-sm text-primary/80 p-0`)}>
                                  {row.linkWithMedia?.label}
                                </span>
                              )}
                              {(row.linkWithMedia?.url ||
                                row.linkWithMedia?.type === 'reference') && (
                                <CMSLink
                                  className="text-sm text-primary/80 hover:font-bold hover:text-primary m-0 h-auto p-0"
                                  {...row.linkWithMedia}
                                />
                              )}
                            </div>
                          </li>
                        )
                      })}

                      {item?.links?.map((link, index) => {
                        return (
                          <li key={index} className="mx-auto md:mx-0 list-none w-fit">
                            <CMSLink
                              className="text-sm text-primary/80 hover:font-bold text-center md:text-left hover:text-primary m-0 h-auto p-0"
                              {...link.link}
                            />
                          </li>
                        )
                      })}
                    </ul>

                    {social && social.length > 0 && (
                      <div className="flex justify-center md:justify-start gap-3 mt-5 pl-2">
                        {social.map((socialNetwork, index) => {
                          return (
                            <a
                              key={index}
                              href={getHref(socialNetwork?.linkWithMedia)}
                              className="flex items-center justify-center w-9 h-9 rounded-full bg-white/40 hover:bg-white/60 transition-colors"
                            >
                              {socialNetwork?.linkWithMedia?.reactIconsIcon && (
                                <Icon
                                  className="w-4 h-4"
                                  reactIconsIcon={socialNetwork.linkWithMedia.reactIconsIcon}
                                />
                              )}
                            </a>
                          )
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )
            })}

            {newsletterForm && (
              <div className="flex flex-col items-center text-center md:items-start md:text-left w-full h-fit">
                <h2 className="font-bold text-base mb-4 text-primary">Newsletter</h2>

                <NewsletterComponent form={newsletterForm} />
              </div>
            )}
          </div>
        </div>

        <div className="container mt-16">
          <div className="flex flex-col-reverse md:flex-row items-center justify-between gap-4 border-t border-primary/15 pt-5 pl-0 lg:pl-3">
            <p className="text-center text-xs text-primary/70 md:text-left">
              {copyrights || `© ${new Date().getFullYear()}. Tous droits réservés`}
              &ensp;- Developed by&ensp;
              <a href="https://humans-it.be/" target="_blank" className="underline">
                humans-it.be
              </a>
            </p>

            {paymentColumn?.paymentMethodsSelect &&
              paymentColumn.paymentMethodsSelect.length > 0 && (
                <div className="flex items-center gap-3">
                  <span className="flex items-center gap-2 rounded-full bg-white/50 px-3 py-1.5 text-xs font-medium text-primary">
                    <Icon reactIconsIcon="lock" className="min-w-0! pt-0!" />
                    Paiement sécurisé
                  </span>
                  <div className="flex items-center gap-1.5">
                    {paymentColumn.paymentMethodsSelect.map((method) => (
                      <span
                        key={method}
                        title={paymentMethodLabels[method]}
                        className="flex h-7 w-9 items-center justify-center rounded bg-white text-primary"
                      >
                        <Icon
                          reactIconsIcon={paymentMethodIcons[method]}
                          className="min-w-0! pt-0! text-sm"
                        />
                      </span>
                    ))}
                  </div>
                </div>
              )}
          </div>
        </div>
      </footer>
    </>
  )
}

export { Footer }
